import { useAppSelector } from "../../hooks";
import classes from "../../styles/Modal.module.css";
import { getClasses } from "../../utils";
import Modal, { ModalProps } from "./Modal";
import Commander from "../../commander";
import { useEffect, useRef, useState } from "react";

type PossibleValues = number[] | { value: string; text: string }[];
type Flow = "column" | "row";
// const SCROLL_DIMMING_TRESHOLD = 0.98;
export type SelectModalProps = ModalProps & {
  data: { value: number | string; possibleValues: PossibleValues };
  flow?: Flow;
  cols?: number;
  rows?: number;
  onSelect?: (value: any) => void;
  soundEffect?: string;
};

const getGridClass = (
  items: number,
  flow: Flow = "column",
  cols?: number,
  rows?: number
): any => {
  if (!flow || (!cols && !rows) || cols === 1) return {};

  const reapeat = (str: string, times: number) =>
    `${str} `.repeat(times).substring(0, times * (str.length + 1) - 1);

  const gridTemplateRows = cols
    ? reapeat("auto", Math.round(items / cols))
    : reapeat("auto", rows ?? 1);

  const gridTemplateColumns = rows
    ? reapeat("auto", Math.round(items / rows))
    : reapeat("auto", cols ?? 1);

  return { gridAutoFlow: flow, gridTemplateColumns, gridTemplateRows };
};

const getValueIndex = (value: number | string, data: PossibleValues) => {
  var i = data.findIndex((possibleValue) => {
    if (typeof possibleValue === "number") {
      return value === possibleValue;
    } else {
      return value === possibleValue.value;
    }
  });
  return i;
};

const SelectModal: React.FC<SelectModalProps> = (props) => {
  const {
    shown,
    title,
    icon,
    data,
    onSelect,
    flow,
    cols,
    rows,
    className,
    soundEffect,
    children,
    onShowStart,
  } = props;
  const valuesRef = useRef(null);
  const selectedRef = useRef(null);
  const selectedValue = useRef(-1);
  // const [scrollClass, setScrollClass] = useState("");
  const modals = useAppSelector((state) => state.modals);
  // if the modal is closing /hide animation is playing or "delayedCloseModal" is dispatched/
  // no new value can be selected
  const selectable = modals.active !== null && !modals.closing && onSelect;
  const onSelectHandler = (value: string | number) => {
    if (selectable) {
      soundEffect && Commander.playSound(soundEffect);
      onSelect(value);
      selectedValue.current = getValueIndex(value, data.possibleValues);
    }
  };
  const style = getGridClass(data.possibleValues.length, flow, cols, rows);
  const gridClass = Object.keys(style).length === 0 ? "" : "grid";
  const disabledClass = selectable ? "" : "disabled";
  const [opacity, setOpacity] = useState([0, 0]);

  const onScroll = () => {
    if (!valuesRef.current) return;
    const el = valuesRef.current as HTMLElement;
    const topOpacity = el.scrollTop / (el.scrollHeight - el.clientHeight);
    if (el.scrollHeight === el.clientHeight || isNaN(topOpacity)) return;

    setOpacity([topOpacity, 1 - topOpacity]);
  };

  const scrollIntoView = () => {
    if (onShowStart) onShowStart();
    if (selectedRef.current != null && shown) {
      (selectedRef.current as HTMLElement).scrollIntoView({ block: "center" });
    }
    onScroll();
  };

  useEffect(() => {
    const scrollIntoViewDelayed = (delay: number) => {
      setTimeout(() => {
        scrollIntoView();
      }, delay);
    };

    scrollIntoViewDelayed(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, rows, cols]);

  useEffect(onScroll, []);

  return (
    <Modal
      {...props}
      className={getClasses(classes, "select", disabledClass, className ?? "")}
      shown={shown}
      icon={icon}
      title={title}
    >
      <div
        style={{ opacity: opacity[0] }}
        className={getClasses(classes, "dimmer", "top")}
      ></div>
      <div
        style={{ opacity: opacity[1] }}
        className={getClasses(classes, "dimmer", "bottom")}
      ></div>
      <div
        ref={valuesRef}
        className={getClasses(classes, "values", gridClass)}
        style={style}
        onScroll={onScroll}
      >
        {data.possibleValues.map((possibleValue, i) => {
          let value, text;
          if (typeof possibleValue === "number") {
            value = text = possibleValue;
          } else {
            value = possibleValue.value;
            text = possibleValue.text;
          }

          return (
            <div
              key={i}
              ref={data.value === value ? selectedRef : null}
              className={getClasses(
                classes,
                "value",
                data.value === value ? "selected" : ""
              )}
              onClick={onSelectHandler.bind(null, value)}
            >
              <div className={classes["value-inner"]}>{text}</div>
            </div>
          );
        })}
      </div>
      {children}
    </Modal>
  );
};

export default SelectModal;
